import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { analytics } from '@/utils/analytics';

interface NestedRouteContext {
  profileId?: string;
  documentId?: string;
  subsidyId?: string;
  breadcrumbs: BreadcrumbItem[];
  updateBreadcrumbs: (items: BreadcrumbItem[]) => void;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
  isActive?: boolean;
}

const NestedRouteContext = createContext<NestedRouteContext | undefined>(undefined);

interface NestedRouteProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that manages hierarchical route context and breadcrumb navigation
 * Supports the new nested URL structure: /profile/:profileId/documents/:documentId
 */
export const NestedRouteProvider: React.FC<NestedRouteProviderProps> = ({ children }) => {
  const location = useLocation();
  const params = useParams();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  // Generate breadcrumbs based on current route
  useEffect(() => {
    const generateBreadcrumbs = (): BreadcrumbItem[] => {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const breadcrumbs: BreadcrumbItem[] = [];

      // Always start with Dashboard
      breadcrumbs.push({
        label: 'Dashboard',
        href: '/dashboard' });

      // Handle hierarchical routes
      for (let i = 0; i < pathSegments.length; i++) {
        const segment = pathSegments[i];
        const isLast = i === pathSegments.length - 1;
        
        switch (segment) {
          case 'profile':
            if (pathSegments[i + 1] && pathSegments[i + 1] !== 'new') {
              const profileId = pathSegments[i + 1];
              breadcrumbs.push({
                label: 'Profile',
                href: `/profile/${profileId}`,
                isActive: isLast || pathSegments[i + 2] === undefined
              });
              i++; // Skip the profileId segment
            } else if (pathSegments[i + 1] === 'new') {
              breadcrumbs.push({
                label: 'New Profile',
                isActive: true
              });
              i++; // Skip 'new'
            }
            break;

          case 'documents':
            breadcrumbs.push({
              label: 'Documents',
              href: params.profileId ? `/profile/${params.profileId}/documents` : '/documents',
              isActive: isLast
            });
            break;

          case 'document-review':
            breadcrumbs.push({
              label: 'Document Review',
              href: params.profileId ? `/profile/${params.profileId}/document-review` : '/document-review',
              isActive: isLast
            });
            break;

          case 'applications':
            breadcrumbs.push({
              label: 'Applications',
              href: params.profileId ? `/profile/${params.profileId}/applications` : '/applications',
              isActive: isLast
            });
            break;

          case 'calendar':
            if (params.profileId) {
              breadcrumbs.push({
                label: 'Calendar',
                href: `/profile/${params.profileId}/calendar`,
                isActive: isLast
              });
            } else {
              breadcrumbs.push({
                label: 'Calendar',
                href: '/calendar',
                isActive: isLast
              });
            }
            break;

          case 'search':
            breadcrumbs.push({
              label: 'Subsidy Search',
              href: '/search',
              isActive: isLast
            });
            break;

          case 'subsidy':
            if (pathSegments[i + 1]) {
              breadcrumbs.push({
                label: 'Subsidy Detail',
                isActive: isLast
              });
              i++; // Skip subsidyId
            }
            break;

          case 'application':
            if (pathSegments[i + 1]) {
              breadcrumbs.push({
                label: 'Application Form',
                isActive: isLast
              });
              i++; // Skip subsidyId
            }
            break;

          case 'admin':
            breadcrumbs.push({
              label: 'Admin',
              href: '/admin',
              isActive: isLast
            });
            
            // Handle admin sub-routes
            if (pathSegments[i + 1]) {
              const adminSubRoute = pathSegments[i + 1];
              switch (adminSubRoute) {
                case 'subsidies':
                  breadcrumbs.push({
                    label: 'Subsidies',
                    href: '/admin/subsidies',
                    isActive: i + 1 === pathSegments.length - 1
                  });
                  break;
                case 'users':
                  breadcrumbs.push({
                    label: 'Users',
                    href: '/admin/users',
                    isActive: i + 1 === pathSegments.length - 1
                  });
                  break;
                case 'system':
                  breadcrumbs.push({
                    label: 'System',
                    href: '/admin/system',
                    isActive: i + 1 === pathSegments.length - 1
                  });
                  break;
                case 'dev':
                  breadcrumbs.push({
                    label: 'Development',
                    href: '/admin/dev',
                    isActive: i + 1 === pathSegments.length - 1
                  });
                  break;
              }
              i++; // Skip admin sub-route
            }
            break;

          case 'help':
            breadcrumbs.push({
              label: 'Help Center',
              href: '/help',
              isActive: isLast
            });
            break;

          default:
            // Handle dynamic segments or unrecognized routes
            if (segment && !segment.match(/^[0-9a-f-]{36}$/)) { // Not a UUID
              breadcrumbs.push({
                label: segment.charAt(0).toUpperCase() + segment.slice(1),
                isActive: isLast
              });
            }
            break;
        }
      }

      return breadcrumbs;
    };

    const newBreadcrumbs = generateBreadcrumbs();
    setBreadcrumbs(newBreadcrumbs);

    // Track route navigation for analytics
    analytics.track('route_navigation', {
      path: location.pathname,
      breadcrumb_count: newBreadcrumbs.length,
      profile_id: params.profileId,
      document_id: params.documentId,
      subsidy_id: params.subsidyId
    });
  }, [location.pathname, params.profileId, params.documentId, params.subsidyId]);

  const updateBreadcrumbs = (items: BreadcrumbItem[]) => {
    setBreadcrumbs(items);
  };

  const contextValue: NestedRouteContext = {
    profileId: params.profileId,
    documentId: params.documentId,
    subsidyId: params.subsidyId,
    breadcrumbs,
    updateBreadcrumbs
  };

  return (
    <NestedRouteContext.Provider value={contextValue}>
      {children}
    </NestedRouteContext.Provider>
  );
};

export const useNestedRoute = (): NestedRouteContext => {
  const context = useContext(NestedRouteContext);
  if (!context) {
    throw new Error('useNestedRoute must be used within a NestedRouteProvider');
  }
  return context;
};

export default NestedRouteProvider;